// MobileRosterEditor
// ---------------------------------------------------------------------------
// Raid-day roster control on a phone. The scenario this exists for: a tank
// no-shows fifteen minutes before invites and the raid leader is nowhere near
// a desktop. Seat someone, bench someone, swap someone, save.
//
// Why not the desktop planner's interaction: it moves players with custom
// mouse events (dragSource / dropTarget), which have no touch equivalent. This
// uses tap-to-select then tap-to-act instead. Tap a seat, pick an action; tap
// an empty seat, pick who fills it.
//
// DELIBERATELY NOT HERE: group reordering, leadership assignment, soft
// reserves, ARC. Those are planning, and planning stays on desktop. This is
// the triage subset.
//
// The one thing to be careful about when editing this file: PUT
// /raids/:id/assignments is a WIPE AND REINSERT of the whole roster, not a
// delta. Anything missing from the payload is deleted, including the bench and
// PUGs the phone never displayed. buildPayload() therefore serialises every
// staged row, and staged rows are seeded from the full assignment list.
import React, { useState, useEffect, useMemo } from "react";
import { useToast } from "./Toast";
import { useTimezone } from "./lib/TimezoneContext";
import { sessionWindowsForRaid } from "./lib/raidWindows";
import { isCharacterAvailable } from "./heuristic_roster";
import {
  getClassColorFromFields,
  getSpecIconUrl,
  getClassIconUrl,
  resolveAssignedSpec,
  roleAccentColor,
  shortRole,
  type DisplayFieldGroup,
} from "./lib/classDisplay";

/** A staged roster row. Mirrors the shape /raids/:id/detail returns. */
export interface RosterRow {
  character_id: string;
  character_name: string;
  character_class_name?: string | null;
  character_metadata?: any;
  character_user_id?: string | null;
  role_label: string | null;
  is_required: boolean;
  metadata: any;
}

interface Candidate {
  characterId: string;
  name: string;
  userId: string;
  ilvl?: number | null;
  isCrossGuild?: boolean;
  sourceGuildName?: string | null;
  sourceGuildTag?: string | null;
  isInterested?: boolean;
  isLockedOut?: boolean;
  isBusy?: boolean;
  isConfirmed?: boolean;
  isPug?: boolean;
  /** 336-bit lockout-week availability string, or null if never set. */
  userAvailability?: string | null;
  metadata?: any;
}

// Codes match auth/roster_event_service.js REMOVAL_REASONS. countsAgainst
// defaults live server side, so this list only needs the code and the label.
const REMOVAL_REASONS: { code: string; label: string }[] = [
  { code: "no_show", label: "No-show" },
  { code: "late", label: "Late" },
  { code: "bench", label: "Benched (comp)" },
  { code: "asked_out", label: "Asked out" },
  { code: "replaced", label: "Replaced" },
  { code: "other", label: "Other" },
];

/**
 * Touch drag has to coexist with scrolling, and the roster is a scrolling
 * list, so a bare drag gesture is ambiguous: the same finger movement means
 * "scroll the list" and "move this player". Long-press to lift disambiguates
 * it, the same way iOS home screen icons and Trello cards work.
 *
 * If the finger travels more than MOVE_TOLERANCE before LONG_PRESS_MS elapses,
 * the gesture was a scroll and the lift is cancelled.
 */
const LONG_PRESS_MS = 220;
const MOVE_TOLERANCE = 10;

type DropTarget = { kind: "seat"; index: number } | { kind: "bench" };

/** Reads the drop target out of the DOM under a point. */
function targetAtPoint(x: number, y: number): DropTarget | null {
  const el = document.elementFromPoint(x, y);
  const zone = el?.closest?.("[data-drop]") as HTMLElement | null;
  if (!zone) return null;
  const raw = zone.dataset.drop || "";
  if (raw === "bench") return { kind: "bench" };
  if (raw.startsWith("seat:")) return { kind: "seat", index: Number(raw.slice(5)) };
  return null;
}

const seatOf = (r: RosterRow): number | null => {
  const s = r.metadata?.seat_index;
  return s === undefined || s === null ? null : Number(s);
};
const isBench = (r: RosterRow): boolean => seatOf(r) === null;

/** Roles a candidate can fill, defaulting to DPS when the character has none. */
const rolesOf = (c: Candidate): string[] => {
  const roles = c.metadata?.role_labels;
  return Array.isArray(roles) && roles.length > 0 ? roles : ["DPS"];
};

export default function MobileRosterEditor({
  raidId,
  raidName,
  raid,
  initialAssignments,
  fields,
  onClose,
  onSaved,
}: {
  raidId: string;
  raidName: string;
  raid: any;
  initialAssignments: RosterRow[];
  fields: DisplayFieldGroup[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const userTz = useTimezone();
  const [rows, setRows] = useState<RosterRow[]>(() => initialAssignments.map(a => ({ ...a, metadata: { ...(a.metadata || {}) } })));
  const [removals, setRemovals] = useState<Record<string, { characterId: string; name: string; reasonCode: string }>>({});
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sheets. Only one is ever open at a time.
  const [actionFor, setActionFor] = useState<RosterRow | null>(null);
  const [picking, setPicking] = useState<{ seatIndex: number | null; replacing: RosterRow | null } | null>(null);
  const [removing, setRemoving] = useState<{ row: RosterRow; reasonCode: string } | null>(null);
  const [roleChoice, setRoleChoice] = useState<{ candidate: Candidate; seatIndex: number | null } | null>(null);
  const [seatPicking, setSeatPicking] = useState<RosterRow | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [search, setSearch] = useState("");
  // Desktop's ignoreAvailability, by another name: reveal candidates who fail
  // the eligibility rules so a leader can seat them anyway.
  const [showAll, setShowAll] = useState(false);

  // Drag state. dragRow is a ref because the document-level move/up listeners
  // are registered once per lift and must not re-bind on every pointermove.
  const [dragActive, setDragActive] = useState(false);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [dragOver, setDragOver] = useState<DropTarget | null>(null);
  const dragRow = React.useRef<RosterRow | null>(null);
  const pressTimer = React.useRef<number | null>(null);
  const pressStart = React.useRef<{ x: number; y: number } | null>(null);
  const didDrag = React.useRef(false);

  useEffect(() => {
    let alive = true;
    fetch(`/raids/${encodeURIComponent(raidId)}/candidates`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (alive) setCandidates(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setCandidates([]); });
    return () => { alive = false; };
  }, [raidId]);

  // Seat count: the raid's configured size, else enough groups to hold whoever
  // is already seated. Never fewer than one group, so an empty raid is fillable.
  const capacity = useMemo(() => {
    const configured = Number(raid?.metadata?.size);
    if (configured > 0) return configured;
    const maxSeat = rows.reduce((m, r) => Math.max(m, seatOf(r) ?? -1), -1);
    return Math.max(5, (Math.floor(maxSeat / 5) + 1) * 5);
  }, [raid, rows]);

  const seated = rows.filter(r => !isBench(r));
  const benched = rows.filter(isBench);
  const bySeat = useMemo(() => {
    const m = new Map<number, RosterRow>();
    seated.forEach(r => m.set(seatOf(r)!, r));
    return m;
  }, [seated]);

  const rosteredIds = useMemo(() => new Set(rows.map(r => String(r.character_id))), [rows]);
  // Bilocation guard: one PLAYER can only be in the raid once, so every
  // character belonging to someone already rostered is ineligible too.
  const rosteredUserIds = useMemo(
    () => new Set(rows.map(r => String(r.character_user_id || "")).filter(Boolean)),
    [rows],
  );
  const raidWindows = useMemo(() => sessionWindowsForRaid(raid, userTz), [raid, userTz]);
  const changeCount = Object.keys(removals).length + (dirty ? 1 : 0);

  // ── Mutations. Each one rebuilds rows immutably and marks dirty. ──────────

  const benchRow = (row: RosterRow) => {
    setRows(prev => prev.map(r => {
      if (String(r.character_id) !== String(row.character_id)) return r;
      const meta = { ...(r.metadata || {}) };
      delete meta.seat_index;
      meta.is_bench = true;
      return { ...r, metadata: meta, is_required: false };
    }));
    setDirty(true);
    setActionFor(null);
  };

  /**
   * Move a player into a seat. Two cases, and they are genuinely different
   * (this mirrors PlannerPage handleDrop cases 1 and 1b):
   *
   *   seat -> seat   Straight swap. The occupant takes the seat the mover just
   *                  vacated, so both players stay seated. Nobody is benched
   *                  and nobody leaves the raid.
   *   bench -> seat  The occupant goes to the bench, because a benched mover
   *                  has no seat to hand back in exchange.
   *
   * Collapsing these into "always bench the occupant" silently demotes someone
   * every time two seated players trade places.
   */
  const seatRow = (rowRef: RosterRow, seatIndex: number) => {
    setRows(prev => {
      // Look the mover up in current state: rowRef may have been captured when
      // the drag started and its seat can be stale by the time it lands.
      const moving = prev.find(r => String(r.character_id) === String(rowRef.character_id));
      if (!moving) return prev;
      const fromSeat = seatOf(moving);
      if (fromSeat === seatIndex) return prev; // dropped back where it started

      const occupant = prev.find(
        r => seatOf(r) === seatIndex && String(r.character_id) !== String(moving.character_id)
      );

      return prev.map(r => {
        if (String(r.character_id) === String(moving.character_id)) {
          const meta = { ...(r.metadata || {}) };
          delete meta.is_bench;
          delete meta.bench_index;
          meta.seat_index = seatIndex;
          return { ...r, metadata: meta, is_required: true };
        }
        if (occupant && String(r.character_id) === String(occupant.character_id)) {
          const meta = { ...(r.metadata || {}) };
          if (fromSeat === null) {
            delete meta.seat_index;
            meta.is_bench = true;
            return { ...r, metadata: meta, is_required: false };
          }
          delete meta.is_bench;
          delete meta.bench_index;
          meta.seat_index = fromSeat;
          return { ...r, metadata: meta, is_required: true };
        }
        return r;
      });
    });
    setDirty(true);
    setActionFor(null);
  };

  const removeRow = (row: RosterRow, reasonCode: string) => {
    setRows(prev => prev.filter(r => String(r.character_id) !== String(row.character_id)));
    setRemovals(prev => ({
      ...prev,
      [String(row.character_id)]: {
        characterId: String(row.character_id),
        name: row.character_name,
        reasonCode,
      },
    }));
    setDirty(true);
    setRemoving(null);
    setActionFor(null);
  };

  const placeCandidate = (c: Candidate, seatIndex: number | null, roleLabel: string) => {
    setRows(prev => {
      let next = prev;
      // Replacing a seat occupant drops them from the roster with reason
      // 'replaced', matching the desktop planner's confirmAssignment.
      if (seatIndex !== null) {
        const occupant = prev.find(r => seatOf(r) === seatIndex);
        if (occupant) {
          setRemovals(rm => ({
            ...rm,
            [String(occupant.character_id)]: {
              characterId: String(occupant.character_id),
              name: occupant.character_name,
              reasonCode: "replaced",
            },
          }));
          next = next.filter(r => String(r.character_id) !== String(occupant.character_id));
        }
      }
      const meta: any = seatIndex === null ? { is_bench: true } : { seat_index: seatIndex };
      if (c.isPug) meta.is_pug = true;
      meta.source = "manual";
      return [...next, {
        character_id: c.characterId,
        character_name: c.name,
        character_class_name: c.metadata?.class_name || null,
        character_metadata: c.metadata || {},
        character_user_id: c.userId,
        role_label: roleLabel,
        is_required: seatIndex !== null,
        metadata: meta,
      }];
    });
    setDirty(true);
    setPicking(null);
    setRoleChoice(null);
    setSearch("");
  };

  // ── Drag to move ─────────────────────────────────────────────────────────

  const cancelPress = () => {
    if (pressTimer.current !== null) { window.clearTimeout(pressTimer.current); pressTimer.current = null; }
    pressStart.current = null;
  };

  const applyDrop = (row: RosterRow, target: DropTarget | null) => {
    if (!target) return;
    if (target.kind === "bench") {
      if (!isBench(row)) benchRow(row);
      return;
    }
    if (seatOf(row) === target.index) return; // dropped back where it started
    seatRow(row, target.index);
  };

  useEffect(() => {
    if (!dragActive) return;

    const onMove = (e: PointerEvent) => {
      setDragPos({ x: e.clientX, y: e.clientY });
      setDragOver(targetAtPoint(e.clientX, e.clientY));
    };
    const onUp = (e: PointerEvent) => {
      const row = dragRow.current;
      if (row) applyDrop(row, targetAtPoint(e.clientX, e.clientY));
      dragRow.current = null;
      setDragActive(false);
      setDragPos(null);
      setDragOver(null);
    };
    const onCancel = () => {
      dragRow.current = null;
      setDragActive(false);
      setDragPos(null);
      setDragOver(null);
    };
    // The lift happens mid-gesture, after the browser has already decided this
    // touch could be a scroll. Nothing short of a non-passive preventDefault
    // stops the list moving under the finger from here.
    const blockScroll = (e: TouchEvent) => e.preventDefault();

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    document.addEventListener("touchmove", blockScroll, { passive: false });
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
      document.removeEventListener("touchmove", blockScroll);
    };
    // applyDrop closes over the current rows via setRows' updater form, so the
    // listeners stay correct for the whole lift without re-registering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragActive]);

  /** Props that make a roster row liftable. Tap still works; drag is additive. */
  const liftHandlers = (row: RosterRow) => ({
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      pressStart.current = { x: e.clientX, y: e.clientY };
      didDrag.current = false;
      pressTimer.current = window.setTimeout(() => {
        dragRow.current = row;
        didDrag.current = true;
        setDragPos(pressStart.current);
        setDragActive(true);
        pressTimer.current = null;
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      // Moving before the lift means the user is scrolling, not dragging.
      if (dragActive || !pressStart.current) return;
      if (Math.abs(e.clientX - pressStart.current.x) > MOVE_TOLERANCE ||
          Math.abs(e.clientY - pressStart.current.y) > MOVE_TOLERANCE) {
        cancelPress();
      }
    },
    onPointerUp: cancelPress,
    onPointerCancel: cancelPress,
    onContextMenu: (e: React.MouseEvent) => { if (didDrag.current) e.preventDefault(); },
  });

  /** A lift that became a drag must not also fire the row's tap action. */
  const tapUnlessDragged = (fn: () => void) => () => {
    if (didDrag.current) { didDrag.current = false; return; }
    fn();
  };

  /** Whole roster, seats and bench alike. See the wipe-and-reinsert note above. */
  const buildPayload = () => rows.map(r => ({
    characterId: r.character_id,
    roleLabel: r.role_label,
    isRequired: !!r.is_required,
    metadata: r.metadata || {},
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/assignments`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assignments: buildPayload(),
          removals: Object.values(removals).map(r => ({ characterId: r.characterId, reasonCode: r.reasonCode })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.error === "forbidden" ? "You can't manage this raid's roster." : "Failed to save the roster.", "error");
        return;
      }
      showToast("Roster saved.", "success");
      onSaved();
    } catch {
      showToast("Network error. The roster was not saved.", "error");
    } finally {
      setSaving(false);
    }
  };

  const attemptClose = () => {
    if (changeCount > 0) setConfirmClose(true);
    else onClose();
  };

  // ── Rendering ────────────────────────────────────────────────────────────

  const renderRow = (r: RosterRow, trailing?: React.ReactNode) => {
    const className = r.character_class_name || r.character_metadata?.class_name || "Unknown";
    const color = getClassColorFromFields(className, fields);
    const spec = resolveAssignedSpec(r);
    const role = roleAccentColor(r.role_label);
    const iconUrl = getSpecIconUrl(spec, className, fields) || getClassIconUrl(className, fields);
    return (
      <>
        <div
          className="relative w-9 h-9 rounded-lg overflow-hidden shrink-0"
          style={{ border: `1px solid ${color}55`, backgroundColor: `${color}1a` }}
        >
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = "none";
                const fb = el.nextElementSibling as HTMLElement | null;
                if (fb) fb.style.display = "flex";
              }}
            />
          ) : null}
          <span
            className="absolute inset-0 items-center justify-center text-xs font-black"
            style={{ display: iconUrl ? "none" : "flex", color }}
          >
            {r.character_name?.[0]?.toUpperCase() || "?"}
          </span>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="font-bold text-sm truncate" style={{ color }}>
            {r.character_name}
            {r.metadata?.is_pug && <span className="text-[10px] text-amber-400 ml-1.5 font-normal">(PUG)</span>}
          </div>
          <div className="text-[11px] font-medium truncate" style={{ color: role }}>
            {spec || className}
          </div>
        </div>
        {trailing ?? (
          <span
            className="shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-md"
            style={{ color: role, backgroundColor: `${role}1a` }}
          >
            {shortRole(r.role_label)}
          </span>
        )}
      </>
    );
  };

  const groupCount = Math.ceil(capacity / 5);

  return (
    <div className="fixed inset-0 z-50 bg-[#09090b] text-white flex flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-white/10 bg-[#0d0d10]">
        <div className="flex items-center gap-2 px-3 py-3">
          <button
            onClick={attemptClose}
            className="px-3 py-2 rounded-lg text-white/55 text-xs font-bold active:bg-white/5"
          >
            Cancel
          </button>
          <div className="flex-1 min-w-0 text-center">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400/70">Manage roster</div>
            <div className="text-sm font-bold truncate">{raidName}</div>
          </div>
          <button
            onClick={save}
            disabled={saving || changeCount === 0}
            className="px-4 py-2 rounded-lg bg-cyan-500 text-black text-xs font-black disabled:opacity-30 disabled:bg-white/10 disabled:text-white/40 active:scale-95 transition-transform"
          >
            {saving ? "Saving" : "Save"}
          </button>
        </div>
        <div className="px-4 pb-2.5 flex items-center gap-3 text-[11px]">
          <span className="text-white/40">
            <span className="font-black text-white/80 tabular-nums">{seated.length}</span>/{capacity} seated
          </span>
          <span className="text-white/40">
            <span className="font-black text-white/80 tabular-nums">{benched.length}</span> benched
          </span>
          {changeCount > 0 ? (
            <span className="ml-auto text-amber-300 font-bold">Unsaved changes</span>
          ) : (
            <span className="ml-auto text-white/25">Hold a player to drag</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-3 pb-8">
        {Array.from({ length: groupCount }, (_, g) => {
          const seatsInGroup = Array.from({ length: 5 }, (_, i) => g * 5 + i).filter(i => i < capacity);
          const filled = seatsInGroup.filter(i => bySeat.has(i)).length;
          return (
            <div key={g} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-transparent">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-black bg-cyan-500/20 text-cyan-300">
                    {g + 1}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-white/70">Group {g + 1}</span>
                </div>
                <span className="text-[10px] font-bold text-white/30">{filled}/{seatsInGroup.length}</span>
              </div>
              <div className="p-2 space-y-1.5">
                {seatsInGroup.map(seatIndex => {
                  const occupant = bySeat.get(seatIndex);
                  const isOver = dragOver?.kind === "seat" && dragOver.index === seatIndex;
                  if (!occupant) {
                    return (
                      <button
                        key={seatIndex}
                        data-drop={`seat:${seatIndex}`}
                        onClick={() => { setSearch(""); setPicking({ seatIndex, replacing: null }); }}
                        aria-label={`Fill empty seat ${seatIndex + 1}`}
                        className={`w-full flex items-center gap-3 py-2.5 px-3.5 rounded-xl border border-dashed transition-colors ${
                          isOver
                            ? "border-cyan-400/70 bg-cyan-500/15 text-cyan-200"
                            : "border-white/15 text-white/30 active:bg-white/5"
                        }`}
                      >
                        <span className="w-9 h-9 rounded-lg border border-dashed border-current grid place-items-center text-lg leading-none opacity-60">+</span>
                        <span className="text-xs font-bold uppercase tracking-wider">
                          {isOver ? "Drop here" : "Empty seat"}
                        </span>
                      </button>
                    );
                  }
                  const lifted = dragActive && dragRow.current?.character_id === occupant.character_id;
                  return (
                    <button
                      key={seatIndex}
                      data-drop={`seat:${seatIndex}`}
                      {...liftHandlers(occupant)}
                      onClick={tapUnlessDragged(() => setActionFor(occupant))}
                      className={`relative w-full flex items-center gap-3 py-2.5 pl-3.5 pr-2.5 rounded-xl border overflow-hidden transition-colors ${
                        isOver && !lifted
                          ? "border-cyan-400/60 bg-cyan-500/15"
                          : "bg-gradient-to-r from-white/[0.05] to-white/[0.01] border-white/5 active:bg-white/[0.08]"
                      } ${lifted ? "opacity-30" : ""}`}
                      style={{ touchAction: dragActive ? "none" : undefined }}
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: roleAccentColor(occupant.role_label) }} />
                      {renderRow(occupant)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Bench */}
        <div
          data-drop="bench"
          className={`rounded-2xl border bg-white/[0.02] overflow-hidden transition-colors ${
            dragOver?.kind === "bench" ? "border-amber-400/60 bg-amber-500/10" : "border-white/10"
          }`}
        >
          <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-white/5 bg-gradient-to-r from-amber-500/10 to-transparent">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-black bg-amber-500/20 text-amber-300">★</span>
              <span className="text-xs font-bold uppercase tracking-wider text-white/70">Bench</span>
            </div>
            <span className="text-[10px] font-bold text-white/30">{benched.length}</span>
          </div>
          <div className="p-2 space-y-1.5">
            {benched.length === 0 && (
              <div className="py-4 text-center text-[11px] text-white/25">Nobody benched.</div>
            )}
            {benched.map(r => {
              const lifted = dragActive && dragRow.current?.character_id === r.character_id;
              return (
                <button
                  key={r.character_id}
                  {...liftHandlers(r)}
                  onClick={tapUnlessDragged(() => setActionFor(r))}
                  className={`relative w-full flex items-center gap-3 py-2.5 pl-3.5 pr-2.5 rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden active:bg-white/[0.06] transition-colors ${lifted ? "opacity-30" : ""}`}
                  style={{ touchAction: dragActive ? "none" : undefined }}
                >
                  {renderRow(r)}
                </button>
              );
            })}
            <button
              onClick={() => { setSearch(""); setPicking({ seatIndex: null, replacing: null }); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-white/15 text-white/35 text-xs font-bold uppercase tracking-wider active:bg-white/5 transition-colors"
            >
              + Add to bench
            </button>
          </div>
        </div>

        {Object.keys(removals).length > 0 && (
          <div className="rounded-2xl border border-red-400/20 bg-red-500/[0.05] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-red-300/70 mb-1.5">
              Removing on save
            </div>
            <div className="text-[11px] text-white/50 leading-relaxed">
              {Object.values(removals).map(r => (
                <div key={r.characterId}>
                  {r.name} <span className="text-white/25">
                    · {REMOVAL_REASONS.find(x => x.code === r.reasonCode)?.label || r.reasonCode}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Drag ghost. pointer-events:none is load-bearing: elementFromPoint has
          to see the drop zone underneath the finger, not this card. */}
      {dragActive && dragPos && dragRow.current && (
        <div
          className="fixed z-[70] pointer-events-none -translate-x-1/2 -translate-y-1/2 w-[70%] max-w-xs"
          style={{ left: dragPos.x, top: dragPos.y }}
        >
          <div className="flex items-center gap-3 py-2.5 pl-3.5 pr-2.5 rounded-xl bg-[#1a1a20] border border-cyan-400/50 shadow-2xl shadow-black/70 scale-105">
            {renderRow(dragRow.current)}
          </div>
        </div>
      )}

      {/* ── Action sheet for a tapped player ──────────────────────────────── */}
      {actionFor && (
        <Sheet onClose={() => setActionFor(null)}>
          <div className="px-4 pb-2">
            <div className="text-base font-black">{actionFor.character_name}</div>
            <div className="text-[11px] text-white/35 mt-0.5">
              {isBench(actionFor) ? "On the bench" : `Group ${Math.floor(seatOf(actionFor)! / 5) + 1}, seat ${(seatOf(actionFor)! % 5) + 1}`}
            </div>
          </div>
          <div className="px-3 pb-3 space-y-2">
            {isBench(actionFor) ? (
              <SheetButton
                label="Move to a seat"
                hint="Pick which seat they take"
                onClick={() => { setSeatPicking(actionFor); setActionFor(null); }}
              />
            ) : (
              <>
                <SheetButton
                  label="Move to bench"
                  hint="Frees their seat, keeps them on the roster"
                  onClick={() => benchRow(actionFor)}
                />
                <SheetButton
                  label="Replace with..."
                  hint="Swap someone else into this seat"
                  onClick={() => { setSearch(""); setPicking({ seatIndex: seatOf(actionFor), replacing: actionFor }); setActionFor(null); }}
                />
              </>
            )}
            <SheetButton
              label="Remove from raid"
              hint="Drops them entirely, with a reason"
              danger
              onClick={() => setRemoving({ row: actionFor, reasonCode: "no_show" })}
            />
          </div>
        </Sheet>
      )}

      {/* ── Removal reason ────────────────────────────────────────────────── */}
      {removing && (
        <Sheet onClose={() => setRemoving(null)}>
          <div className="px-4 pb-3">
            <div className="text-base font-black">Remove {removing.row.character_name}?</div>
            <div className="text-[11px] text-white/35 mt-1 leading-relaxed">
              The reason is logged against attendance, so pick the one that fits.
            </div>
          </div>
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {REMOVAL_REASONS.map(r => {
              const sel = removing.reasonCode === r.code;
              return (
                <button
                  key={r.code}
                  onClick={() => setRemoving({ ...removing, reasonCode: r.code })}
                  className={`px-3 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${
                    sel ? "bg-red-500/20 border-red-400/45 text-red-200" : "bg-white/[0.03] border-white/10 text-white/45"
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <div className="px-3 pb-3 flex gap-2">
            <button
              onClick={() => setRemoving(null)}
              className="flex-1 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white/55 text-sm font-bold"
            >
              Cancel
            </button>
            <button
              onClick={() => removeRow(removing.row, removing.reasonCode)}
              className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-400/40 text-red-200 text-sm font-bold"
            >
              Remove
            </button>
          </div>
        </Sheet>
      )}

      {/* ── Candidate picker ──────────────────────────────────────────────── */}
      {picking && (
        <Sheet onClose={() => { setPicking(null); setSearch(""); }} tall>
          <div className="px-4 pb-2">
            <div className="text-base font-black">
              {picking.replacing
                ? `Replace ${picking.replacing.character_name}`
                : picking.seatIndex === null ? "Add to bench" : `Fill seat ${(picking.seatIndex % 5) + 1}`}
            </div>
            <div className="text-[11px] text-white/35 mt-0.5">
              {picking.replacing ? "They come off the roster as 'replaced'." : "Players available for this raid's time."}
            </div>
          </div>
          <div className="px-4 pb-2 flex items-center justify-between">
            <span className="text-[11px] text-white/35">
              {showAll ? "Showing everyone" : "Available only"}
            </span>
            <button
              onClick={() => setShowAll(v => !v)}
              role="switch"
              aria-checked={showAll}
              aria-label="Show players who are not available"
              className="flex items-center gap-2"
            >
              <span className="text-[11px] font-bold text-white/45">Show all</span>
              <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                showAll ? "bg-amber-500" : "bg-white/10 ring-1 ring-inset ring-white/15"
              }`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${showAll ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
          </div>
          <div className="px-3 pb-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name"
              className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/10 focus:border-white/25 focus:outline-none text-sm text-white/85 placeholder-white/25"
            />
          </div>
          <div className="px-3 pb-3 space-y-1.5 overflow-y-auto">
            {candidates === null ? (
              <div className="py-8 text-center text-white/30 text-sm">Loading...</div>
            ) : (() => {
              const q = search.trim().toLowerCase();
              const pool = candidates
                .filter(c => !rosteredIds.has(String(c.characterId)))
                .filter(c => !q || c.name.toLowerCase().includes(q))
                .map(c => ({ c, reason: ineligibilityReason(c, { rosteredUserIds, raidWindows }) }));

              const eligible = pool.filter(x => !x.reason);
              const rest = pool.filter(x => x.reason);
              // Default to the same pool the desktop planner offers. The
              // override exists because raid night sometimes needs a body in
              // the seat regardless, which is also why desktop has
              // ignoreAvailability, but it should never be the default.
              const list = (showAll ? [...eligible, ...rest] : eligible)
                .sort((a, b) => Number(!!a.reason) - Number(!!b.reason) || a.c.name.localeCompare(b.c.name));

              if (list.length === 0) {
                return (
                  <div className="py-8 text-center">
                    <div className="text-white/30 text-sm">
                      {rest.length > 0 ? "Nobody available for this raid's time." : "Nobody left to add."}
                    </div>
                    {rest.length > 0 && !showAll && (
                      <div className="text-[11px] text-white/20 mt-1.5">
                        {rest.length} {rest.length === 1 ? "player is" : "players are"} filtered out.
                      </div>
                    )}
                  </div>
                );
              }
              return list.map(({ c, reason: warn }) => {
                const className = c.metadata?.class_name || "Unknown";
                const color = getClassColorFromFields(className, fields);
                const spec = c.metadata?.primary_spec_label || null;
                const iconUrl = getSpecIconUrl(spec, className, fields) || getClassIconUrl(className, fields);
                return (
                  <button
                    key={c.characterId}
                    onClick={() => {
                      const roles = rolesOf(c);
                      if (roles.length === 1) placeCandidate(c, picking.seatIndex, roles[0]);
                      else setRoleChoice({ candidate: c, seatIndex: picking.seatIndex });
                    }}
                    className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-xl border transition-colors ${
                      warn
                        ? "bg-amber-500/[0.04] border-amber-400/20 active:bg-amber-500/10"
                        : "bg-white/[0.03] border-white/[0.07] active:bg-white/[0.08]"
                    }`}
                  >
                    <div
                      className="relative w-9 h-9 rounded-lg overflow-hidden shrink-0"
                      style={{ border: `1px solid ${color}55`, backgroundColor: `${color}1a` }}
                    >
                      {iconUrl ? (
                        <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-black" style={{ color }}>
                          {c.name[0]?.toUpperCase() || "?"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-bold truncate" style={{ color }}>
                        {c.name}
                        {c.isCrossGuild && c.sourceGuildTag && (
                          <span className="ml-1.5 text-[10px] font-normal text-cyan-300/70">[{c.sourceGuildTag}]</span>
                        )}
                      </div>
                      <div className="text-[11px] text-white/35 truncate">
                        {spec || className}
                        {rolesOf(c).length > 0 && <span className="text-white/20"> · {rolesOf(c).join(", ")}</span>}
                      </div>
                      {warn && <div className="text-[10px] text-amber-400/90 truncate mt-0.5">{warn}</div>}
                    </div>
                    {c.isConfirmed && !warn && (
                      <span className="shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-300">
                        Confirmed
                      </span>
                    )}
                  </button>
                );
              });
            })()}
          </div>
        </Sheet>
      )}

      {/* ── Seat picker for a benched player ──────────────────────────────
          Taking an occupied seat is the raid-day case (bench healer goes in
          for the healer who dropped), so occupied seats are selectable and
          the occupant falls back to the bench rather than off the roster. */}
      {seatPicking && (
        <Sheet onClose={() => setSeatPicking(null)} tall>
          <div className="px-4 pb-3">
            <div className="text-base font-black">Seat {seatPicking.character_name}</div>
            <div className="text-[11px] text-white/35 mt-0.5">
              Taking an occupied seat sends its player to the bench.
            </div>
          </div>
          <div className="px-3 pb-3 space-y-1.5 overflow-y-auto">
            {Array.from({ length: capacity }, (_, i) => i).map(seatIndex => {
              const occupant = bySeat.get(seatIndex);
              const isSelf = occupant && String(occupant.character_id) === String(seatPicking.character_id);
              return (
                <button
                  key={seatIndex}
                  disabled={!!isSelf}
                  onClick={() => { seatRow(seatPicking, seatIndex); setSeatPicking(null); }}
                  aria-label={`Seat ${seatIndex + 1}${occupant ? `, taken by ${occupant.character_name}` : ", empty"}`}
                  className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-xl border transition-colors disabled:opacity-30 ${
                    occupant
                      ? "bg-white/[0.03] border-white/[0.07] active:bg-white/[0.08]"
                      : "bg-cyan-500/[0.06] border-cyan-400/20 active:bg-cyan-500/15"
                  }`}
                >
                  <span className="w-9 h-9 rounded-lg grid place-items-center text-[11px] font-black bg-white/5 text-white/50 shrink-0">
                    {Math.floor(seatIndex / 5) + 1}.{(seatIndex % 5) + 1}
                  </span>
                  <span className="flex-1 min-w-0 text-left">
                    {occupant ? (
                      <>
                        <span className="block text-sm font-bold truncate text-white/85">{occupant.character_name}</span>
                        <span className="block text-[11px] text-white/30">
                          {isSelf ? "Already here" : "Will move to the bench"}
                        </span>
                      </>
                    ) : (
                      <span className="block text-sm font-bold text-cyan-300">Empty</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </Sheet>
      )}

      {/* ── Role choice for multi-role candidates ─────────────────────────── */}
      {roleChoice && (
        <Sheet onClose={() => setRoleChoice(null)}>
          <div className="px-4 pb-3">
            <div className="text-base font-black">Which role?</div>
            <div className="text-[11px] text-white/35 mt-0.5">{roleChoice.candidate.name} can fill more than one.</div>
          </div>
          <div className="px-3 pb-3 space-y-2">
            {rolesOf(roleChoice.candidate).map(role => (
              <button
                key={role}
                onClick={() => placeCandidate(roleChoice.candidate, roleChoice.seatIndex, role)}
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl bg-white/[0.04] border border-white/10 active:bg-white/[0.09] transition-colors"
              >
                <span className="w-2 h-8 rounded-full" style={{ backgroundColor: roleAccentColor(role) }} />
                <span className="text-sm font-bold">{role}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {/* ── Discard guard ─────────────────────────────────────────────────── */}
      {confirmClose && (
        <Sheet onClose={() => setConfirmClose(false)}>
          <div className="px-4 pb-3">
            <div className="text-base font-black">Discard changes?</div>
            <div className="text-[11px] text-white/35 mt-1 leading-relaxed">
              Nothing has been sent to the server yet. Closing now loses the edits.
            </div>
          </div>
          <div className="px-3 pb-3 flex gap-2">
            <button
              onClick={() => setConfirmClose(false)}
              className="flex-1 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white/55 text-sm font-bold"
            >
              Keep editing
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-400/40 text-red-200 text-sm font-bold"
            >
              Discard
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/**
 * Why this candidate should not be seated in this raid, or null if they are
 * clear. This is the same constraint set the desktop planner applies (see
 * PlannerPage handleAutoFill, "ROSTER_POPULATION.md 2.4"):
 *
 *   alt-of-rostered (bilocation) - isInterested - isBusy - isLockedOut -
 *   isConfirmed - isCharacterAvailable against the raid's session windows.
 *
 * Ordered most-disqualifying first, so a candidate who fails several reads as
 * the worst one. The time check comes last because it is the softest: a
 * player who did not fill in availability is a different problem from one who
 * is already saved to the instance.
 */
export function ineligibilityReason(
  c: Candidate,
  ctx: { rosteredUserIds: Set<string>; raidWindows: [number, number][] | null },
): string | null {
  if (c.userId && ctx.rosteredUserIds.has(String(c.userId))) return "Another of their characters is on the roster";
  if (c.isLockedOut) return "Already saved to this instance";
  if (c.isBusy) return "In another raid at this time";
  if (c.isInterested === false) return "Not signed up for this raid";
  if (c.isConfirmed === false) return "Availability not confirmed";
  if (!isCharacterAvailable({ id: c.characterId, availabilityBits: c.userAvailability } as any, ctx.raidWindows)) {
    return "Not available at the raid time";
  }
  return null;
}

/** Bottom sheet with a grabber, backdrop dismiss and safe-area padding. */
function Sheet({ children, onClose, tall }: { children: React.ReactNode; onClose: () => void; tall?: boolean }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end" onClick={onClose}>
      <div
        className={`bg-[#121214] w-full rounded-t-2xl border-t border-white/10 flex flex-col ${tall ? "max-h-[85vh]" : "max-h-[70vh]"} overflow-hidden`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2.5 pb-2 shrink-0">
          <span className="w-10 h-1 rounded-full bg-white/20" />
        </div>
        {children}
      </div>
    </div>
  );
}

function SheetButton({ label, hint, onClick, danger }: {
  label: string; hint: string; onClick: () => void; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
        danger
          ? "bg-red-500/[0.07] border-red-400/25 active:bg-red-500/15"
          : "bg-white/[0.04] border-white/10 active:bg-white/[0.09]"
      }`}
    >
      <div className={`text-sm font-bold ${danger ? "text-red-300" : "text-white"}`}>{label}</div>
      <div className="text-[11px] text-white/35 mt-0.5">{hint}</div>
    </button>
  );
}
